/* eslint-disable max-len */

import { PolymerElement } from "@polymer/polymer/polymer-element.js";
import template from "./ns-image.html";
import { ImageService } from "@nuskin/ns-util";
import "@nuskin/ns-style-elements";

/**
 * The ns-image tag puts the heavy lifting of image optimization on the browser,
 * allowing the browser to choose the most efficient image to download, while also taking
 * advantage of some of the nice css object-fit and object-position properties.
 *
 * <h1>Demo</h1>
 * <demo-snippet class="demoSnippet">
 *  <ns-image src="https://via.placeholder.com/200x200" size-lock="-1"></ns-image>
 * </demo-snippet>
 */
export class NsImage extends PolymerElement {
  constructor() {
    super();
  }
  ready() {
    super.ready();
    this.updateStyles({
      "--ns-image-object-fit": this.imageFit,
      "--ns-image-object-position": this.imagePosition
    });
  }

  static get is() {
    return "ns-image";
  }

  static get template() {
    let htmlElement = document.createElement("TEMPLATE");
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get properties() {
    return {
      /**
       * url for the image without a renditions path param. If renditions
       * are specified in this url, it will pass through as the single src. Use careful judgement
       * if making such a request.
       */
      src: {
        type: String,
        observer: "_setImage"
      },
      /**
       * create a source set for the given image url.
       */
      useSrcSet: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * set image position (css object-position). Valid values are strings like 'left center', 'center bottom'
       * and so on. This value is passed directly to the object-position property. We recommend not using 'px px'
       * or '% %' strings to avoid strange behavior.
       */
      imagePosition: {
        type: String,
        value: "center"
      },
      /**
       * set image fit within the container (css object-fit). Valid values are:
       * contain, cover, fill, none, scale-down
       */
      imageFit: {
        type: String,
        value: "cover"
      },
      /**
       * if the size of image is known ahead of time or wanted, but the rendition url is not present
       * this will request a specific image size or smaller than the number requested (if not smallest).
       * images are close to the following dimensions, however they maintain aspect ratio when resized and
       * may not be square as the numbers suggest.
       * values are the following numbers:
       *  -1: original image
       *   0: thumbnail 48x48
       *   1: thumbnail 140x100
       *   2: small     319x319
       *   3: phone     540x540
       *   4: tablet    720x720
       *   5: large     1280X1280
       */
      sizeLock: {
        type: Number,
        value: -1
      },
      /**
       * directs the srcset string to not include thumbnail sized images.
       */
      noThumbnail: {
        type: Boolean,
        value: true
      },
      /**
       * directs the srcset string to include only thumbnail sized images.
       */
      thumbnailOnly: {
        type: Boolean,
        value: false
      },
      /**
       * if provided will override the default image breakpoint suggestions in the sizes array
       */
      sizesArray: {
        type: String,
        value:
          "(max-device-width: 480px) 100px, (max-device-width: 1024px) 300px, 1000px"
      },
      /**
       * if provided, this will be added to the object-fit/object-contain style string. This style is applied directly
       * to the img tag.
       */
      styleOverride: {
        type: String,
        value: ""
      },
      /**
       * If provided, this will make the width of the image auto instead of 100%
       */
      fluid: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * when provided adds the alt tag for screen readers
       */
      altText: {
        type: String,
        value: ""
      },
      /**
       * Aspect ratios for the image
       * Values are: 1x1 / 4:3 / 16:9 / 21:9
       */
      maintainRatio: {
        type: String,
        value: ""
      },
      /**
       * Fallback image if first fails
       */
      fallbackSrc: {
        type: String
      }
    };
  }

  _setImage() {
    // Protect against an undefined this.originalUrl
    this.src = this.src ? this.src.replace(/ /g, "%20") : "";

    if (this.maintainRatio && this.maintainRatio.length > 0) {
      const tempstring = this.useSrcSet
        ? ImageService.getSizedURL(this.src, this.sizeLock)
        : this.src.length
          ? this.src
          : this.fallbackSrc;
      this.updateStyles({ "--ns-image-bg": "url(" + tempstring + ")" });
      this.setAltText(tempstring);
      return;
    }

    this.singleSrcUrl = this.src;
    //clean up invalid urls (no spaces allowed)
    if (this.singleSrcUrl) {
      this.singleSrcUrl = this.singleSrcUrl.replace(/ /g, "%20");
    }
    this.setAltText(this.singleSrcUrl);

    let el = this.$.imagecontainer;
    if (el) {
      el.onload = function() {
        el.removeAttribute("datasrc");
      };
      //always set the src attribute as a fallback
      el.setAttribute(
        "src",
        ImageService.getSizedURL(this.singleSrcUrl, this.sizeLock)
      );
      if (this.useSrcSet && this.sizeLock < 0) {
        el.setAttribute(
          "srcset",
          ImageService.createSRCSET(this.singleSrcUrl, this.noThumbnail)
        );
      }
      el.onerror = function() {
        el.removeAttribute("srcset");
        if (!this.useSrcSet && this.fallbackSrc)
          el.setAttribute("src", this.fallbackSrc);
      };
    }
  }
  getStyle() {
    return `${this.styleOverride}`;
  }
  setAltText(imageUrl) {
    if (
      this.altText &&
      this.altText.length <= 0 &&
      typeof imageUrl === "string"
    )
      this.altText = imageUrl
        .substring(imageUrl.lastIndexOf("/") + 1, imageUrl.lastIndexOf("."))
        .replace(/-/g, " ");
  }
}
customElements.define(NsImage.is, NsImage);
